import { Clear, Search, AddOutlined as AddOutlinedIcon } from "@mui/icons-material";
import {
	Autocomplete,
	Box,
	Button,
	FormControl,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	SelectChangeEvent,
	TextField,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";

import { useProfile } from "../../../context/ProfileContext";
import { ToastContent } from "../../../shared/components";
import { useDebounce, useToast } from "../../../shared/hooks";
import { clientService } from "../../../shared/services/api/client/clientService";
import { PermissionServices } from "../../../shared/services/api/permissions/PermissionService";
import { returnMessageError } from "../../../shared/utils/returnMessageError";

export type ToolbarUsersOption = {
	id: string;
	label: string;
};

interface ToolbarUsersProps {
	busca: string;
	showSearch: boolean;
	onChangeBusca: (newText: string) => void;
	onSearch: () => void;
	onClearSearch: () => void;

	textoButtonNew: string;
	showButtonNew: boolean;
	onClickNovo: () => void;
	isButtonNewDisabled?: boolean;

	statusValue: string;
	showInputStatus: boolean;
	onChangeStatus: (status: string) => void;

	clientIdValue: string;
	showInputClientFilter: boolean;
	onChangeClientFilter: (clientId: string) => void;

	permissionDescriptionValue: string;
	showInputPermissionFilter: boolean;
	isPermissionFilterDisabled?: boolean;
	onChangePermissionFilter: (permissionDescription: string) => void;
}

export const ToolbarUsers: React.FC<ToolbarUsersProps> = ({
	busca,
	showSearch,
	onChangeBusca,
	onSearch,
	onClearSearch,
	textoButtonNew,
	showButtonNew,
	onClickNovo,
	isButtonNewDisabled,
	statusValue,
	showInputStatus,
	onChangeStatus,
	clientIdValue,
	showInputClientFilter,
	onChangeClientFilter,
	permissionDescriptionValue,
	showInputPermissionFilter,
	isPermissionFilterDisabled,
	onChangePermissionFilter,
}) => {
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
	const { debounce } = useDebounce();
	const toastHandler = useToast();
	const { userClientId, isSuperAdmin, permissionLevel } = useProfile();

	const [clientInput, setClientInput] = useState("");
	const [clientOptions, setClientOptions] = useState<ToolbarUsersOption[]>([]);
	const [clientLoading, setClientLoading] = useState(false);

	const [permissionOptions, setPermissionOptions] = useState<ToolbarUsersOption[]>([]);
	const [permissionLoading, setPermissionLoading] = useState(false);

	const selectedClient = useMemo(() => {
		if (!clientIdValue) return null;
		return clientOptions.find((o) => o.id === clientIdValue) ?? null;
	}, [clientIdValue, clientOptions]);

	type PermissionSelectOption = ToolbarUsersOption;
	const selectedPermission = useMemo(() => {
		if (!permissionDescriptionValue) return null;
		return permissionOptions.find((o) => o.label === permissionDescriptionValue) ?? null;
	}, [permissionDescriptionValue, permissionOptions]);

	useEffect(() => {
		if (!showInputClientFilter) return;
		if (!clientInput) {
			setClientOptions([]);
			return;
		}

		setClientLoading(true);
		debounce(() => {
			clientService
				.getSelect(1, clientInput.replace("(Inativo)", "").trim(), "", "", "", isSuperAdmin || permissionLevel == 2 ? "" : userClientId)
				.then((result: any) => {
					if (result instanceof Error) return;

					const options = result?.data?.map((client: any) => ({
						id: client.id,
						label: client.status == "inactive" ? `${client.name} (Inativo)` : client.name,
					}));

					setClientOptions(options ?? []);
				})
				.catch((err: any) => {
					const { titleMessage, contentMessage } = returnMessageError(err);
					toastHandler.present({
						type: "error",
						position: "top-right",
						durationMs: 4000,
						messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
					});
				})
				.finally(() => setClientLoading(false));
		});
	}, [clientInput, showInputClientFilter]);

	useEffect(() => {
		if (!showInputPermissionFilter) return;
		if (isPermissionFilterDisabled) return;

		setPermissionLoading(true);
		PermissionServices.getUnlimited(isSuperAdmin || permissionLevel == 2 ? 1 : Number(permissionLevel))
			.then((result: any) => {
				if (result instanceof Error) return;

				const options = result?.data?.map((permission: any) => ({
					id: permission.id,
					label: permission.description,
				}));
				setPermissionOptions(options ?? []);
			})
			.catch((err: any) => {
				const { titleMessage, contentMessage } = returnMessageError(err);
				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
				});
			})
			.finally(() => setPermissionLoading(false));
	}, [showInputPermissionFilter, isPermissionFilterDisabled]);

	const handleChangeStatus = (event: SelectChangeEvent) => {
		onChangeStatus(event.target.value as string);
	};

	return (
		<Box component={Paper} display="flex" flexDirection="column" padding={2} gap={1} mx={1} elevation={0}>
			{/* Linha 1: filtros + botão novo */}
			<Grid container spacing={2} direction={isSmallScreen ? "column" : "row"} alignItems={isSmallScreen ? "stretch" : "center"}>
				{showInputStatus && (
					<Grid item xs={12} sm={6} md={2}>
						<FormControl fullWidth size="small">
							<InputLabel id="status-users">Status</InputLabel>
							<Select
								labelId="status-users"
								id="status-users"
								fullWidth
								label="Status"
								value={statusValue || "Todos"}
								onChange={handleChangeStatus}
							>
								<MenuItem value="Todos">Todos</MenuItem>
								<MenuItem value="active">Ativo</MenuItem>
								<MenuItem value="blocked">Bloqueado</MenuItem>
								<MenuItem value="inactive">Inativo</MenuItem>
							</Select>
						</FormControl>
					</Grid>
				)}

				{showInputClientFilter && (
					<Grid item xs={12} sm={6} md={3}>
						<Autocomplete
							fullWidth
							size="small"
							loading={clientLoading}
							options={clientOptions}
							value={selectedClient}
							onInputChange={(_, value) => setClientInput(value)}
							onChange={(_, value) => onChangeClientFilter(value?.id ?? "")}
							renderInput={(params) => <TextField {...params} label="Cliente" />}
						/>
					</Grid>
				)}

				{showInputPermissionFilter && (
					<Grid item xs={12} sm={6} md={3}>
						<Autocomplete<PermissionSelectOption>
							fullWidth
							size="small"
							disabled={isPermissionFilterDisabled}
							loading={permissionLoading}
							options={permissionOptions}
							value={selectedPermission}
							onChange={(_, value) => onChangePermissionFilter(value?.label ?? "")}
							renderInput={(params) => <TextField {...params} label="Perfis" />}
						/>
					</Grid>
				)}

				{showButtonNew && (
					<Grid item xs={12} sm="auto" md="auto" sx={{ ml: { md: "auto" } }}>
						<Box display="flex" justifyContent={isSmallScreen ? "flex-start" : "flex-end"}>
							<Button
								variant="contained"
								color="primary"
								startIcon={<AddOutlinedIcon />}
								onClick={onClickNovo}
								disabled={isButtonNewDisabled}
							>
								{textoButtonNew}
							</Button>
						</Box>
					</Grid>
				)}
			</Grid>

			{/* Linha 2: pesquisa + botão pesquisar */}
			{showSearch && (
				<Grid container spacing={2} direction={isSmallScreen ? "column" : "row"} alignItems={isSmallScreen ? "stretch" : "center"}>
					<Grid item xs={12} sm={6} md={3}>
						<TextField
							fullWidth
							size="small"
							value={busca}
							placeholder="Pesquisar..."
							onChange={(e) => onChangeBusca(e.target.value)}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<Search />
									</InputAdornment>
								),
								endAdornment: (
									<InputAdornment position="end">
										<IconButton onClick={onClearSearch} disabled={!busca}>
											<Clear />
										</IconButton>
									</InputAdornment>
								),
							}}
						/>
					</Grid>

					<Grid item xs={12} sm="auto" md="auto">
						<Box display="flex" justifyContent="flex-start" gap={1}>
							<Button variant="contained" onClick={onSearch}>
								Pesquisar
							</Button>
						</Box>
					</Grid>
				</Grid>
			)}
		</Box>
	);
};
